import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay, switchMap } from 'rxjs/operators';

import { ApplicationSettings, ApplicationSettingService } from 'tms-lib';

import { AccountingPackages } from '../../../constants';
import { CreditCheck } from '../../../models';
import { BexioService } from '../bexio';
import { XeroService } from '../xero';

/**
 * Sends an accounting READ to whichever package is configured, so the screens that ask for one
 * do not each have to know which packages exist.
 *
 * The credit check is called from four places - Customer, Sales Order, Sales Order Details and
 * Manage Sales Order. Before Bexio they all called XeroService directly, which meant a Bexio
 * site's credit check went to Xero and could not work: the Xero call reads Client_ID and
 * ClientSecret off the selling company's accounting connection, and a Bexio connection holds a
 * Personal Access Token instead. Putting the choice here rather than in each screen keeps it to
 * one place, and stops the next screen that needs a credit check from re-introducing the bug.
 *
 * Posting still branches inside the posting modals: those already have the package to hand, and
 * they differ by more than which service to call (Xero chains an upload-and-email step, Sage
 * only produces a CSV).
 */
@Injectable({
  providedIn: 'root',
})
export class AccountingService {
  private readonly applicationSettingService = inject(ApplicationSettingService);
  private readonly xeroService = inject(XeroService);
  private readonly bexioService = inject(BexioService);

  // Shared between everything in flight at once, but NOT cached for the life of the app.
  //
  // ⚠️ refCount must stay true. shareReplay replays a failure to every later subscriber just as
  // it replays a value, so caching for the session means one blip on the settings call leaves
  // the credit check dead on every screen until the user reloads - and looking like a credit
  // check fault rather than a settings one. Letting the buffer reset once nothing is subscribed
  // costs one small settings read per credit check, which is what each screen did for itself
  // before this service existed.
  private readonly accountingPackage$ = this.applicationSettingService
    .get(ApplicationSettings.accountingPackage)
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  /**
   * The customer's ledger and credit verdict, from the configured accounting package.
   *
   * Anything that is not Bexio goes to Xero, which is exactly what happened before this service
   * existed - so Sage sites behave as they always have rather than changing under this fix.
   */
  getCreditCheck(id: string): Observable<CreditCheck> {
    return this.accountingPackage$.pipe(
      switchMap((accountingPackage: string) =>
        accountingPackage === AccountingPackages.bexio
          ? this.bexioService.getCreditCheck(id)
          : this.xeroService.getCreditCheck(id),
      ),
    );
  }

  /**
   * Ask the accounting package which of these invoices have been paid. The API stores
   * Paid/DatePaid back on each invoice, so the caller just reloads its grid.
   */
  checkPaid(ids: string[]): Observable<void> {
    return this.accountingPackage$.pipe(
      switchMap((accountingPackage: string) =>
        accountingPackage === AccountingPackages.bexio
          ? this.bexioService.checkPaid(ids)
          : this.xeroService.checkPaid(ids),
      ),
    );
  }
}
